/**
 * BackgroundChecksTab - Background checks management tab for AdminStaff page
 */
import { useState } from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { 
  Shield, CheckCircle, XCircle, Clock, AlertCircle, 
  RefreshCw, CheckSquare, Loader2
} from "lucide-react";
import { BgCheckBadge } from "./StatusBadges";

const BackgroundChecksTab = ({
  backgroundChecks,
  bgCheckSummary,
  bgCheckFilter,
  setBgCheckFilter,
  selectedChecks,
  setSelectedChecks,
  searchQuery,
  onRefreshCheck,
  onBulkAction,
  bulkActionLoading
}) => {
  // Filter background checks
  const filteredChecks = backgroundChecks.filter(check => {
    const matchesSearch = !searchQuery || 
      `${check.first_name} ${check.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      check.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = bgCheckFilter === "all" || check.status === bgCheckFilter;
    return matchesSearch && matchesFilter;
  });

  // Toggle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedChecks(filteredChecks.map(c => c.check_id));
    } else {
      setSelectedChecks([]);
    }
  };

  // Toggle individual check
  const toggleCheck = (checkId) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId)
        : [...prev, checkId]
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "clear": return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case "pending": return <Clock className="w-4 h-4 text-amber-600" />;
      case "consider": return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* BG Check Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total", value: bgCheckSummary.total, color: "text-slate-700", filter: "all" },
          { label: "Pending", value: bgCheckSummary.pending, color: "text-amber-600", filter: "pending" },
          { label: "Clear", value: bgCheckSummary.clear, color: "text-emerald-600", filter: "clear" },
          { label: "Consider", value: bgCheckSummary.consider, color: "text-orange-600", filter: "consider" },
          { label: "Failed", value: bgCheckSummary.failed, color: "text-red-600", filter: "failed" }
        ].map(stat => (
          <button
            key={stat.filter}
            onClick={() => setBgCheckFilter(stat.filter)}
            className={`p-4 rounded-xl border-2 transition-all bg-white ${
              bgCheckFilter === stat.filter 
                ? 'border-slate-900 shadow-md' 
                : 'border-transparent hover:border-slate-200'
            }`}
            data-testid={`bg-filter-${stat.filter}`}
          >
            <p className="text-sm text-slate-500">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </button>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedChecks.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-xl">
          <span className="text-sm font-medium text-slate-700">
            {selectedChecks.length} selected
          </span>
          <Button 
            size="sm" 
            onClick={() => onBulkAction("refresh")}
            disabled={bulkActionLoading}
            className="bg-slate-900 rounded-full"
            data-testid="bulk-refresh"
          >
            {bulkActionLoading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Refresh Status
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setSelectedChecks([])}
            className="rounded-full"
            data-testid="clear-selection"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Checks Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filteredChecks.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No background checks</h3>
            <p className="text-slate-500">Background checks will appear here when coaches are approved</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 w-10">
                  <Checkbox 
                    checked={selectedChecks.length === filteredChecks.length && filteredChecks.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checks"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Person</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Submitted</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Completed</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredChecks.map((check) => (
                <tr 
                  key={check.check_id} 
                  className="hover:bg-slate-50"
                  data-testid={`bg-check-row-${check.check_id}`}
                >
                  <td className="px-4 py-4">
                    <Checkbox 
                      checked={selectedChecks.includes(check.check_id)}
                      onCheckedChange={() => toggleCheck(check.check_id)}
                      data-testid={`select-check-${check.check_id}`}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-medium text-slate-900">{check.first_name} {check.last_name}</p>
                    <p className="text-xs text-slate-500">{check.email}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
                      {check.person_type || "Coach"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <BgCheckBadge status={check.status} />
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-500">
                    {check.submitted_at ? new Date(check.submitted_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-500">
                    {check.completed_at ? new Date(check.completed_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onRefreshCheck(check.check_id)}
                        className="rounded-lg"
                        data-testid={`refresh-check-${check.check_id}`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BackgroundChecksTab;
